/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react/destructuring-assignment */
import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  TileGroup, ListItem, Button
} from '@one-thd/sui-atomic-components';
import { extend } from '@thd-nucleus/data-sources';
import { FitCompatibility } from '@thd-olt-component-react/fit-compatibility';
import { ArrowDown, ArrowUp } from '@one-thd/sui-icons';
import { DimensionSearchField } from './DimensionSearchField';
import { StandardRefinement } from '../Refinements/StandardRefinement';
import { OnDisplayRefinement } from '../Refinements/OnDisplayRefinement';
import { PriceRangeRefinement } from '../Refinements/PriceRangeRefinement';
import { sortRefinements, isRefinementActive, extractNavParamFromClearAllURL } from '../../product-results-helpers';
import { ProductResultsContext } from '../ProductResultsContext';
import { getInitialItemsToShowCount } from '../../utils/showMoreLessUtility';

const StandardDimension = ({
  dimension,
  hasSearchBar,
  mini,
  onChange,
  onDisplayData,
  enableMultiStore,
  priceRange,
  toggleSwitch,
  onMultiStoreSelectionChange,
  multiStoreSelection,
  multiFacet,
  fitCompatibility,
  parentID,
  selectedRefinements
}) => {

  const [selectedRefinementData, setSelectedRefinementData] = useState({
    label: null,
    refinementKey: null
  });

  const { data } = useContext(ProductResultsContext);
  const navParam = extractNavParamFromClearAllURL(data);

  const [filterInput, setFilterInput] = useState('');
  const [showMore, setShowMore] = useState(false);

  const handleSearchInputChange = (event) => {
    setFilterInput(event.target.value);
  };

  const getRefinementId = (refinement, refinementIndex) => {
    const { refinementKey } = refinement;
    return mini
      ? `${refinementKey}-${refinementIndex}-mini`
      : `${refinementKey}-${refinementIndex}`;
  };

  const { label = '', refinements = [] } = dimension || {};
  const nestedRefinements = refinements || [];

  const initialItemsToShowCount = getInitialItemsToShowCount(dimension, nestedRefinements);

  const refinementsToShow = showMore ? nestedRefinements.length : initialItemsToShowCount;
  const showMoreItemCount = nestedRefinements.length - initialItemsToShowCount;
  const sortedRefinements = [...nestedRefinements].sort(sortRefinements);
  const visibleRefinements = sortedRefinements.slice(0, refinementsToShow);

  if (label.toUpperCase() === 'AVAILABILITY' && onDisplayData.isStoreDisplay === 'true') {
    return null;
  }

  const showListItemClassNames = classNames(
    'sui-flex',
    'sui-justify-center',
    'sui-items-center',
    'sui-relative',
    'sui-underline',
    'sui-w-full',
    'sui-box-border',
    'sui-text-center',
    'sui-font-regular',
    'sui-text-base',
    'sui-normal-case',
    'sui-pb-1',
    'sui-pt-2'
  );

  const showButtonClassNames = classNames(
    'sui-inline-flex',
    'sui-items-center',
    'sui-cursor-pointer',
    'sui-p-0',
    'sui-bg-transparent',
    'sui-border-0',
    'sui-w-auto'
  );

  const showArrowIconClassNames = classNames(
    'sui-w-4',
    'sui-h-4',
    'sui-flex-shrink-0'
  );

  const handleSeeMore = (event) => {
    event.preventDefault();
    setShowMore(!showMore);
  };

  const handleRefinementTileClick = (_event, selectedRefinement) => {
    if (!selectedRefinement) {
      return;
    }
    setSelectedRefinementData({
      label: selectedRefinement.label,
      refinementKey: selectedRefinement.refinementKey
    });

    if (typeof onChange === 'function') {
      onChange({ refinement: selectedRefinement, dimension });
    }
  };

  const renderRefinements = (refinementList) => {
    return (
      <TileGroup
        value={selectedRefinementData.label}
        aria-label={`${label} Options`}
        orientation="vertical"
        onChange={handleRefinementTileClick}
        grow
        key={selectedRefinementData.refinementKey}
        data-testid="refinement-secondary-drawer"
        data-instoredisplay="true"
      >
        {refinementList
          .filter((ref) => (ref.label || '').toUpperCase().indexOf(filterInput.toUpperCase()) !== -1)
          .sort(sortRefinements)
          .map((refinement, refinementIndex) => {
            const id = getRefinementId(refinement, refinementIndex);
            const isRefinementSelected = isRefinementActive(
              refinement.refinementKey,
              selectedRefinements
            );

            if (refinement.label.toUpperCase() === 'PICK UP TODAY' && onDisplayData.isStoreDisplay === 'true') {
              return (
                <OnDisplayRefinement
                  key={id}
                  {...{
                    dimension,
                    id,
                    onChange,
                    refinement,
                    toggleSwitch,
                    onMultiStoreSelectionChange,
                    multiStoreSelection,
                    enableMultiStore,
                    onDisplayData,
                    multiFacet,
                    parentID
                  }}
                />
              );
            }

            return (
              <StandardRefinement
                key={id}
                {...{
                  dimension,
                  id,
                  onChange,
                  enableMultiStore,
                  refinement,
                  toggleSwitch,
                  onMultiStoreSelectionChange,
                  multiStoreSelection,
                  onDisplay: onDisplayData.onDisplay,
                  multiFacet,
                  isRefinementSelected,
                  parentID
                }}
              />
            );
          })}
      </TileGroup>
    );
  };

  return (
    <>
      {!toggleSwitch && (
        <>
          {hasSearchBar && (
            <DimensionSearchField
              label={label}
              value={filterInput}
              onChange={handleSearchInputChange}
            />
          )}
          {priceRange && (
            <PriceRangeRefinement dimension={dimension} onChange={onChange} />
          )}
          {fitCompatibility && (
            <div
              role="button"
              tabIndex={0}
              aria-label="fit-compatibility"
              className="sui-mb-3"
            >
              <FitCompatibility
                type="MINI"
                drawerPosition="left"
                imageCacheEnabled
                navParam={navParam}
                data={data}
              />
            </div>
          )}
          {visibleRefinements && renderRefinements(visibleRefinements)}
        </>
      )}
      {toggleSwitch && (
        <div>
          {visibleRefinements && renderRefinements(visibleRefinements)}
        </div>
      )}
      {nestedRefinements.length > initialItemsToShowCount && (
        <ListItem className={showListItemClassNames}>
          <Button
            variant="text"
            onClick={handleSeeMore}
            aria-label={showMore
              ? 'Show fewer refinements'
              : `Show ${showMoreItemCount} more refinements`}
            className={showButtonClassNames}
          >
            <span>
              {showMore ? 'Show Less' : `Show ${showMoreItemCount} More`}
            </span>
            <span className="sui-ml-2 sui-mt-1">
              {showMore ? <ArrowUp className={showArrowIconClassNames} />
                : <ArrowDown className={showArrowIconClassNames} />}
            </span>
          </Button>
        </ListItem>
      )}
    </>
  );
};

StandardDimension.propTypes = {
  dimension: PropTypes.shape({
    dimensionId: PropTypes.string,
    label: PropTypes.string,
    refinements: PropTypes.arrayOf(
      PropTypes.shape({})
    ),
    nestedRefinementsLimit: PropTypes.number
  }).isRequired,
  hasSearchBar: PropTypes.bool,
  fitCompatibility: PropTypes.bool,
  mini: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onMultiStoreSelectionChange: PropTypes.func,
  enableMultiStore: PropTypes.bool,
  priceRange: PropTypes.bool,
  multiStoreSelection: PropTypes.arrayOf(PropTypes.string),
  onDisplayData: PropTypes.shape({
    onDisplay: PropTypes.string,
    onDisplayChange: PropTypes.func,
    isStoreDisplay: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool
    ]),
    itemCount: PropTypes.string
  }),
  toggleSwitch: PropTypes.bool,
  multiFacet: PropTypes.bool,
  selectedRefinements: PropTypes.arrayOf(PropTypes.shape({})),
  parentID: PropTypes.string
};

StandardDimension.defaultProps = {
  hasSearchBar: false,
  fitCompatibility: false,
  mini: false,
  onDisplayData: {
    onDisplay: 'false',
    onDisplayChange: null,
    isStoreDisplay: 'false',
    itemCount: null
  },
  priceRange: false,
  toggleSwitch: false,
  enableMultiStore: false,
  onMultiStoreSelectionChange: null,
  multiStoreSelection: null,
  multiFacet: false,
  selectedRefinements: null,
  parentID: ''
};

StandardDimension.displayName = 'StandardDimension';

StandardDimension.dataModel = extend(StandardRefinement);

export { StandardDimension };
